import { NgModule } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import {
  MAT_COLOR_FORMATS,
  NGX_MAT_COLOR_FORMATS
} from '@angular-material-components/color-picker';
import {
  it_IT,
  NZ_DATE_CONFIG,
  NZ_I18N,
  NzDateConfig
} from 'ng-zorro-antd/i18n';
import it from '@angular/common/locales/it';

/* GIF */
import { LottieAnimationViewModule } from 'ng-lottie';
import { LottieModule } from 'ngx-lottie';

import { SharedModule } from '../shared/shared.module';
import { LoaderInterceptor } from '../interceptors/loader.interceptor';

// Core layout components
import { SecureComponent } from './secure.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { SidebarComponent } from './dashboard/sidebar/sidebar.component';
import { FooterComponent } from './dashboard/footer/footer.component';
import { HeaderComponent } from './dashboard/header/header.component';
import { NewSidebarComponent } from './dashboard/new-sidebar/new-sidebar.component';
import { NewHeaderComponent } from './dashboard/new-header/new-header.component';
import { SectionModalComponent } from './dashboard/new-sidebar/section-modal/section-modal.component';

// Core pages (always needed)
import { BachecaComponent } from './dashboard/bacheca/bacheca.component';
import { ProfiloUtenteComponent } from './dashboard/profilo-utente/profilo-utente.component';
import { FeedbackComponent } from './dashboard/feedback/feedback.component';
import { NewTimesheetComponent } from './dashboard/new-timesheet/new-timesheet.component';

// Team pages
import { IlMioTeamComponent } from './dashboard/il-mio-team/il-mio-team.component';
import { ScrumComponent } from './dashboard/components/card-lista-teams/scrum/scrum.component';
import { ScrumBillingComponent } from './dashboard/components/card-lista-teams/scrum/scrum-billing/scrum-billing.component';
import { ConcludiCollaborazioneComponent } from './dashboard/components/card-lista-teams/scrum/concludi-collaborazione/concludi-collaborazione.component';

// Other always-needed components
import { ReviewComponent } from './dashboard/components/review/review.component';
import { CardNotificheComponent } from './dashboard/components/card-notifiche/card-notifiche.component';
import { StoricoTeamPassatiComponent } from './dashboard/components/storico-team-passati/storico-team-passati.component';

// Card/profile components used in bacheca/profilo
import { CardDatiGeneraliComponent } from './dashboard/components/card-dati-generali/card-dati-generali.component';
import { CardListaTeamsComponent } from './dashboard/components/card-lista-teams/card-lista-teams.component';
import { CardGamificationComponent } from './dashboard/components/card-gamification/card-gamification.component';
import { CardComeStaAndandoComponent } from './dashboard/components/card-come-sta-andando/card-come-sta-andando.component';
import { HobbiesComponent } from './dashboard/components/card-hobbies/hobbies/hobbies.component';
import { CardConvinzioniComponent } from './dashboard/components/card-limitazioni/card-limitazioni.component';
import { CardMembroTeamComponent } from './dashboard/components/card-membro-team/card-membro-team.component';
import { StatisticheProgettoComponent } from './dashboard/components/statistiche-progetto/statistiche-progetto.component';
import { ModaleStoryPointsComponent } from './dashboard/components/modale-story-points/modale-story-points.component';
import { NewGamificationCardComponent } from './dashboard/components/new-gamification-card/new-gamification-card.component';
import { ProgressBarGamificationComponent } from './dashboard/components/new-gamification-card/progress-bar-gamification/progress-bar-gamification.component';

// Profile sub-components
import { ModificaPasswordComponent } from './dashboard/profilo-utente/modifica-password/modifica-password.component';

// Timesheet sub-components
import { TimelineComponent } from './dashboard/timeline/timeline.component';
import { TimeSheetComponent } from './dashboard/time-sheet/time-sheet.component';
import { ModalRequestTimesheetComponent } from './dashboard/new-timesheet/modal-request-timesheet/modal-request-timesheet.component';
import { ModalNoteTimesheetComponent } from './dashboard/new-timesheet/modal-note/modal-note-timesheet.component';

// Calendar components
import { CalendarComponent } from './dashboard/components/calendar/calendar.component';
import { DeleteEventModalComponent } from './dashboard/components/calendar/delete-event-modal/delete-event-modal.component';
import { ModalDesiredSmartComponent } from './dashboard/components/calendar/modal-desired-smart/modal-desired-smart.component';
import { ModalRejectedRequestComponent } from './dashboard/components/calendar/modal-rejected-request/modal-rejected-request.component';
import { HolidayRequestsComponent } from './dashboard/components/calendar/request-modal/HolidayRequests/HolidayRequests.component';
import { RequestPermissionComponent } from './dashboard/components/calendar/request-modal/RequestModal/RequestPermission.component';
import { SicknessRequestComponent } from './dashboard/components/calendar/request-modal/SicknessRequest/SicknessRequest.component';
import { WorkplaceRequestComponent } from './dashboard/components/calendar/request-modal/WorkplaceRequest/WorkplaceRequest.component';
import { SecondStepSicknessComponent } from './dashboard/components/calendar/request-modal/second-step/second-step-sickness/second-step-sickness.component';
import { ReviewModalComponent } from './dashboard/components/calendar/review-modal/review-modal.component';
import { SidebarCalendarComponent } from './dashboard/components/calendar/sidebar-calendar/sidebar-calendar.component';
import { UserCalendarComponent } from './dashboard/components/calendar/user-calendar/user-calendar.component';
import { CalendarSmartComponent } from './dashboard/components/calendar-smart/calendar-smart.component';
import { ModalDayTypeComponent } from './dashboard/components/calendar-smart/modal-day-type/modal-day-type.component';
import { ModalGenerateMonthComponent } from './dashboard/components/calendar-smart/modal-generate-month/modal-generate-month.component';
import { ModalMaxSmartComponent } from './dashboard/components/calendar-smart/modal-max-smart/modal-max-smart.component';
import { ModalSeatsComponent } from './dashboard/components/calendar-smart/modal-seats/modal-seats.component';

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

const customDateConfig: NzDateConfig = {
  firstDayOfWeek: 1 // lunedì
};

export function playerFactory(): any {
  return import('lottie-web');
}

registerLocaleData(it);

@NgModule({
  declarations: [
    SecureComponent,
    DashboardComponent,
    SidebarComponent,
    FooterComponent,
    HeaderComponent,
    NewSidebarComponent,
    NewHeaderComponent,
    SectionModalComponent,
    BachecaComponent,
    ProfiloUtenteComponent,
    FeedbackComponent,
    NewTimesheetComponent,
    IlMioTeamComponent,
    ScrumComponent,
    ScrumBillingComponent,
    ConcludiCollaborazioneComponent,
    ReviewComponent,
    CardNotificheComponent,
    StoricoTeamPassatiComponent,
    CardDatiGeneraliComponent,
    CardListaTeamsComponent,
    CardGamificationComponent,
    CardComeStaAndandoComponent,
    HobbiesComponent,
    CardConvinzioniComponent,
    CardMembroTeamComponent,
    StatisticheProgettoComponent,
    ModaleStoryPointsComponent,
    NewGamificationCardComponent,
    ProgressBarGamificationComponent,
    ModificaPasswordComponent,
    TimelineComponent,
    TimeSheetComponent,
    ModalRequestTimesheetComponent,
    ModalNoteTimesheetComponent,
    CalendarComponent,
    DeleteEventModalComponent,
    ModalDesiredSmartComponent,
    ModalRejectedRequestComponent,
    HolidayRequestsComponent,
    RequestPermissionComponent,
    SicknessRequestComponent,
    WorkplaceRequestComponent,
    SecondStepSicknessComponent,
    ReviewModalComponent,
    SidebarCalendarComponent,
    UserCalendarComponent,
    CalendarSmartComponent,
    ModalDayTypeComponent,
    ModalGenerateMonthComponent,
    ModalMaxSmartComponent,
    ModalSeatsComponent
  ],
  imports: [
    SharedModule,
    RouterModule,
    LottieAnimationViewModule.forRoot(),
    LottieModule.forRoot({ player: playerFactory })
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true
    },
    { provide: MAT_COLOR_FORMATS, useValue: NGX_MAT_COLOR_FORMATS },
    { provide: NZ_I18N, useValue: it_IT },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: NZ_DATE_CONFIG, useValue: customDateConfig }
  ]
})
export class SecureModule {}
